import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getHospital,
  getHospitalEmailNotifications,
  Hospital,
  EmailNotification,
} from "../../services/superAdmin.service";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  User,
  Globe,
  Database,
  Clock,
  Hash,
  Tag,
} from "lucide-react";

const HospitalDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [loading, setLoading] = useState(true);

  // Email notifications (activation email status)
  const [emailNotifications, setEmailNotifications] = useState<EmailNotification[]>([]);

  const loadHospital = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      const data = await getHospital(id!);
      setHospital(data);
    } catch {
      toast.error("Hospital not found");
      navigate("/hospitals");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const loadEmailNotifications = async () => {
    try {
      const list = await getHospitalEmailNotifications(id!);
      setEmailNotifications(list);
    } catch {
      setEmailNotifications([]);
    }
  };

  useEffect(() => {
    loadHospital(true);
    loadEmailNotifications();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (!hospital) return null;

  return (
    <div className="max-w-5xl mx-auto space-y-6 px-4 sm:px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Info Card */}
        <div className="md:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2 pb-4 border-b border-gray-50">
            <Building2 size={18} className="text-gray-400" />
            General Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-6">
            <Info icon={<Hash size={14} />} label="Hospital Code" value={hospital.hospitalCode} />
            <Info icon={<Tag size={14} />} label="Hospital Type" value={hospital.hospitalType} />
            <Info icon={<Building2 size={14} />} label="Registration No" value={hospital.registrationNumber} />
            <Info icon={<Mail size={14} />} label="Official Email" value={hospital.officialEmail} />
            <Info icon={<Phone size={14} />} label="Official Phone" value={hospital.officialPhone} />
            <Info icon={<Globe size={14} />} label="Domain Name" value={hospital.domainName} />
            <Info icon={<Database size={14} />} label="Database Name" value={hospital.dbName} />
            <Info icon={<Clock size={14} />} label="Time Zone" value={hospital.timezone} />
            <div className="sm:col-span-2">
              <Info
                icon={<MapPin size={14} />}
                label="Full Address"
                value={[hospital.addressline1, hospital.addressline2, hospital.city, hospital.state, hospital.country, hospital.postcode].filter(Boolean).join(", ")}
              />
            </div>
          </div>

          {/* Contact Person */}
          {(hospital.contactPersonName || hospital.contactPersonEmail || hospital.contactPersonPhone) && (
            <div className="mt-6 pt-6 border-t border-gray-50">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <User size={14} />
                Contact Person
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                <Info label="Name" value={hospital.contactPersonName} />
                <Info icon={<Mail size={14} />} label="Email" value={hospital.contactPersonEmail} />
                <Info icon={<Phone size={14} />} label="Phone" value={hospital.contactPersonPhone} />
              </div>
            </div>
          )}
        </div>

        {/* Side Cards */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">System Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">Access</span>
                <StatusBadge status={hospital.status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">Onboarding</span>
                <OnboardingBadge status={hospital.onboardingStatus} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-sm">Activation Email</span>
                <EmailStatusBadge notification={emailNotifications[0] || null} />
              </div>
            </div>
          </div>

          {/* Meta Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900 font-medium">
                  {hospital.createdAt ? new Date(hospital.createdAt).toLocaleDateString() : "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Subscribed</span>
                <span className={`font-medium ${hospital.isSubscribed ? "text-green-600" : "text-gray-500"}`}>
                  {hospital.isSubscribed ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---

const EmailStatusBadge = ({ notification }: { notification?: EmailNotification | null }) => {
  if (!notification) {
    return <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-500">N/A</span>;
  }
  let classes = "bg-gray-100 text-gray-600";
  switch (notification.status) {
    case "SENT": classes = "bg-green-100 text-green-700"; break;
    case "FAILED": classes = "bg-red-100 text-red-700"; break;
    case "PENDING":
    case "RETRY": classes = "bg-yellow-100 text-yellow-700"; break;
  }
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${classes}`}>{notification.status}</span>;
};

const Info = ({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value?: string | null;
}) => (
  <div className="group">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1">
      {icon}
      {label}
    </p>
    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-700 transition-colors break-words">
      {value || "—"}
    </p>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  let classes = "bg-gray-100 text-gray-600";
  switch (status?.toUpperCase()) {
    case "ACTIVE": classes = "bg-green-100 text-green-700"; break;
    case "INACTIVE": classes = "bg-gray-100 text-gray-500"; break;
  }
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${classes}`}>{status}</span>;
};

const OnboardingBadge = ({ status }: { status: string }) => {
  let classes = "bg-gray-100 text-gray-600";
  switch (status?.toUpperCase()) {
    case "COMPLETED": classes = "bg-green-100 text-green-700"; break;
    case "IN_PROGRESS": classes = "bg-blue-100 text-blue-700"; break;
    case "NOT_STARTED": classes = "bg-gray-100 text-gray-500"; break;
  }
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${classes}`}>{status?.replace(/_/g, " ")}</span>;
};

export default HospitalDetails;
